import { useMemo, useState } from "react";
import Papa from "papaparse";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { UploadSimple, Download, CheckCircle, XCircle, MinusCircle, Stack } from "@phosphor-icons/react";

const REQUIRED = ["Variant SKU", "Option1 Value", "Variant Inventory Qty", "Expiry Date"];
const TEMPLATE = `Variant SKU,Option1 Value,Variant Inventory Qty,Expiry Date,Location
TWVWECH02152105,1.52m x 1.05m,3,2027-04-15,Shelf A1
TWVWECH02152505,1.52m x 5.05m,2,2027-04-15,Shelf A1
TWVWECH021521805,1.52m x 18.05m,1,2027-04-15,Warehouse 2`;

function parseRows(text) {
  const cleaned = (text || "").replace(/^\ufeff/, "");
  const result = Papa.parse(cleaned, { header: true, skipEmptyLines: "greedy", transformHeader: (h) => h.trim() });
  const headers = (result.meta.fields || []).map((h) => h.trim());
  const rows = (result.data || []).map((r) => {
    const o = {};
    for (const h of headers) o[h] = (r[h] ?? "").toString().trim();
    return o;
  });
  return { headers, rows };
}

export default function BulkInventoryDialog({ onSuccess }) {
  const [open, setOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [batchPrefix, setBatchPrefix] = useState(`APR${new Date().getFullYear()}`);
  const [skipUnknown, setSkipUnknown] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const parsed = useMemo(() => {
    if (!csvText.trim()) return { headers: [], rows: [], error: null };
    const { headers, rows } = parseRows(csvText);
    const missing = REQUIRED.filter((h) => !headers.includes(h));
    if (missing.length) {
      return { headers, rows, error: `Missing required columns: ${missing.join(", ")}` };
    }
    return { headers, rows, error: null };
  }, [csvText]);

  // Compute pre-import summary: actionable rows + total rolls that will be created
  const summary = useMemo(() => {
    const r = parsed.rows || [];
    let plannedRolls = 0;
    let actionable = 0;
    let blank = 0;
    for (const row of r) {
      const qty = parseInt(row["Variant Inventory Qty"], 10);
      const exp = (row["Expiry Date"] || "").trim();
      if (!row["Variant Inventory Qty"]?.trim()) { blank++; continue; }
      if (!isNaN(qty) && qty > 0 && exp) { plannedRolls += qty; actionable++; }
    }
    return { totalRows: r.length, actionable, plannedRolls, blank };
  }, [parsed.rows]);

  const fileStats = useMemo(() => {
    if (!csvText) return null;
    return {
      lineCount: (csvText.match(/\n/g) || []).length + 1,
      sizeKb: (new Blob([csvText]).size / 1024).toFixed(1),
    };
  }, [csvText]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const text = await f.text();
      setCsvText(text);
      const sizeKb = (f.size / 1024).toFixed(1);
      const lineCount = (text.match(/\n/g) || []).length + 1;
      toast.success(`Loaded ${f.name} (${sizeKb} KB · ${lineCount} lines)`);
    } catch (err) { toast.error(`Failed to read file: ${err.message}`); }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "vinyl-inventory-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const onImport = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/rolls/bulk-update", {
        csv_text: csvText,
        default_batch_prefix: batchPrefix || "BULK",
        skip_unknown_skus: skipUnknown,
      });
      setResult(data);
      toast.success(`Created ${data.rolls_created_count} rolls (${data.rows_skipped_count} skipped, ${data.rows_error_count} errors)`);
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  const reset = () => { setCsvText(""); setResult(null); };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2 rounded-md" data-testid="bulk-inventory-btn">
          <Stack size={16} /> Bulk Update Inventory
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Stack size={20} /> Bulk Update Inventory
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-5 py-2">
            <div className="border border-blue-200 bg-blue-50 rounded-md p-3 text-xs text-slate-700">
              <div className="font-medium text-slate-900 mb-1">How this works</div>
              For each row, the system finds the matching product (via Variant SKU + parsed dimensions) and creates <strong>N</strong> new roll instances where <strong>N = Variant Inventory Qty</strong>. The roll's <strong>manufacturing_date</strong> is set to <strong>Expiry Date − 3 years</strong>. Add an optional <strong>Location</strong> column to place the rolls (defaults to the product's default location). A <strong>Handle</strong> column is not needed — rows are matched by Variant SKU. Empty Qty rows are skipped. Products must already exist (run the Shopify product import first if needed).
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={downloadTemplate} className="gap-2 rounded-md text-xs h-8" data-testid="download-inv-template">
                <Download size={12} /> Download template
              </Button>
              <span className="text-xs text-slate-500">Required: <code className="font-mono text-[11px]">{REQUIRED.join(", ")}</code> · Optional: <code className="font-mono text-[11px]">Location</code></span>
            </div>

            <Tabs defaultValue="upload">
              <TabsList>
                <TabsTrigger value="upload" data-testid="inv-tab-upload">Upload File</TabsTrigger>
                <TabsTrigger value="paste" data-testid="inv-tab-paste">Paste CSV</TabsTrigger>
              </TabsList>
              <TabsContent value="upload" className="space-y-2">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Upload .csv file</Label>
                <input type="file" accept=".csv,text/csv" onChange={onFile}
                  className="block w-full text-sm border border-slate-200 rounded-md p-2 file:mr-4 file:py-2 file:px-3 file:border-0 file:text-xs file:font-medium file:bg-slate-100 hover:file:bg-slate-200"
                  data-testid="inv-file-input" />
              </TabsContent>
              <TabsContent value="paste" className="space-y-2">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Paste CSV (with header row)</Label>
                <Textarea rows={8} value={csvText} onChange={(e) => setCsvText(e.target.value)} placeholder={TEMPLATE} className="font-mono text-xs" data-testid="inv-csv-textarea" />
              </TabsContent>
            </Tabs>

            {/* File stats */}
            {fileStats && (
              <div className="grid grid-cols-3 gap-0 border border-slate-200 bg-white text-xs" data-testid="inv-stats">
                <div className="p-3 border-r border-slate-200">
                  <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">File size</div>
                  <div className="font-mono text-base font-semibold mt-0.5 tabular-nums">{fileStats.sizeKb} KB</div>
                </div>
                <div className="p-3 border-r border-slate-200">
                  <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Lines</div>
                  <div className="font-mono text-base font-semibold mt-0.5 tabular-nums">{fileStats.lineCount.toLocaleString()}</div>
                </div>
                <div className="p-3">
                  <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Rows parsed</div>
                  <div className="font-mono text-base font-semibold mt-0.5 tabular-nums">{(parsed.rows || []).length.toLocaleString()}</div>
                </div>
              </div>
            )}

            {parsed.error && (
              <div className="border border-red-200 bg-red-50 text-red-700 rounded-md p-3 text-sm">{parsed.error}</div>
            )}

            {/* Plan summary */}
            {summary.totalRows > 0 && !parsed.error && (
              <div className="border border-slate-200 bg-slate-50 rounded-md p-4">
                <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Pre-import plan</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="bg-white border border-slate-200 rounded-md p-3">
                    <div className="text-xs text-slate-600">Actionable rows</div>
                    <div className="font-mono text-2xl font-semibold mt-0.5 tabular-nums">{summary.actionable.toLocaleString()}</div>
                  </div>
                  <div className="bg-white border border-emerald-200 rounded-md p-3">
                    <div className="text-xs text-slate-600">Rolls to create</div>
                    <div className="font-mono text-2xl font-semibold mt-0.5 tabular-nums text-emerald-700">{summary.plannedRolls.toLocaleString()}</div>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-md p-3">
                    <div className="text-xs text-slate-600">Empty Qty (skipped)</div>
                    <div className="font-mono text-2xl font-semibold mt-0.5 tabular-nums text-slate-500">{summary.blank.toLocaleString()}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Options */}
            {summary.totalRows > 0 && !parsed.error && (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="border border-slate-200 rounded-md p-3 bg-white">
                  <Label className="text-[10px] font-mono tracking-[0.15em] uppercase text-slate-500">Batch prefix</Label>
                  <Input value={batchPrefix} onChange={(e) => setBatchPrefix(e.target.value)} className="font-mono h-8 mt-1" data-testid="inv-batch-prefix" />
                  <div className="text-[10px] text-slate-500 mt-1">Used in roll batch numbers, e.g. {batchPrefix || "BULK"}-2027-04-01</div>
                </div>
                <div className="flex items-center justify-between border border-slate-200 rounded-md p-3 bg-white">
                  <div>
                    <div className="font-medium">Skip unknown SKUs</div>
                    <div className="text-[10px] text-slate-500">Otherwise treat as errors</div>
                  </div>
                  <Switch checked={skipUnknown} onCheckedChange={setSkipUnknown} data-testid="inv-skip-unknown" />
                </div>
              </div>
            )}

            {/* Preview */}
            {parsed.rows.length > 0 && !parsed.error && (
              <div className="border border-slate-200 rounded-md">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-mono tracking-[0.15em] uppercase text-slate-600 flex justify-between">
                  <span>Preview</span><span>showing first 8 of {parsed.rows.length}</span>
                </div>
                <div className="overflow-x-auto max-h-72 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                      <tr className="text-left">
                        <th className="px-3 py-2">Variant SKU</th>
                        <th className="px-3 py-2">Size</th>
                        <th className="px-3 py-2 text-right">Qty</th>
                        <th className="px-3 py-2">Expiry</th>
                        <th className="px-3 py-2">Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.rows.slice(0, 8).map((r, i) => {
                        const qty = parseInt(r["Variant Inventory Qty"], 10);
                        const isAction = !isNaN(qty) && qty > 0 && r["Expiry Date"];
                        return (
                          <tr key={i} className={`border-t border-slate-100 ${isAction ? "" : "text-slate-400"}`}>
                            <td className="px-3 py-1.5 font-mono">{r["Variant SKU"]}</td>
                            <td className="px-3 py-1.5">{r["Option1 Value"]}</td>
                            <td className={`px-3 py-1.5 font-mono text-right ${isAction ? "font-semibold text-emerald-700" : ""}`}>{r["Variant Inventory Qty"] || "—"}</td>
                            <td className="px-3 py-1.5">{r["Expiry Date"] || "—"}</td>
                            <td className="px-3 py-1.5">{r["Location"] || "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-3 gap-0 border border-slate-200">
              <div className="p-4 border-r border-slate-200">
                <div className="flex items-center gap-2 text-emerald-600"><CheckCircle weight="fill" size={18} /></div>
                <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mt-1">Rolls created</div>
                <div className="font-mono text-3xl font-semibold mt-1 text-emerald-700 tabular-nums">{result.rolls_created_count}</div>
              </div>
              <div className="p-4 border-r border-slate-200">
                <div className="flex items-center gap-2 text-slate-500"><MinusCircle weight="fill" size={18} /></div>
                <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mt-1">Skipped rows</div>
                <div className="font-mono text-3xl font-semibold mt-1 text-slate-700 tabular-nums">{result.rows_skipped_count}</div>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 text-red-600"><XCircle weight="fill" size={18} /></div>
                <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mt-1">Errors</div>
                <div className="font-mono text-3xl font-semibold mt-1 text-red-600 tabular-nums">{result.rows_error_count}</div>
              </div>
            </div>

            {result.rows_errors?.length > 0 && (
              <div>
                <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Errors</div>
                <div className="border border-red-200 rounded-md max-h-40 overflow-y-auto">
                  {result.rows_errors.slice(0, 50).map((e, i) => (
                    <div key={i} className="px-3 py-2 border-b last:border-0 border-red-100 text-xs">
                      <span className="font-mono text-slate-500">Row {e.row}:</span> <span className="font-mono">{e.sku}</span> — <span className="text-red-700">{e.error}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {result.rows_skipped?.length > 0 && (
              <div>
                <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Skipped</div>
                <div className="border border-slate-200 rounded-md max-h-40 overflow-y-auto">
                  {result.rows_skipped.slice(0, 50).map((s, i) => (
                    <div key={i} className="px-3 py-2 border-b last:border-0 border-slate-100 text-xs">
                      <span className="font-mono text-slate-500">Row {s.row}:</span> <span className="font-mono">{s.sku}</span> — {s.reason}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-md">Cancel</Button>
              <Button
                onClick={onImport}
                disabled={submitting || summary.plannedRolls === 0 || !!parsed.error}
                className="bg-blue-600 hover:bg-blue-700 rounded-md gap-2"
                data-testid="inv-import-confirm"
              >
                <UploadSimple size={16} /> {submitting ? "Importing…" : `Create ${summary.plannedRolls} rolls`}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={reset} className="rounded-md" data-testid="inv-import-another">Update another batch</Button>
              <Button onClick={() => setOpen(false)} className="bg-blue-600 hover:bg-blue-700 rounded-md">Done</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
